/**
 * Inline autocomplete plugin. Reads suggestion sources from the document's
 * <lists> plus a live scan of the body, computes suggestions for the current
 * element/caret (suggestions.ts), and renders a tap-to-accept dropdown. It only
 * intercepts keys while the dropdown is open, so it never steals Enter from the
 * element keymap otherwise.
 */
import { Plugin, PluginKey, type EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import type { Node as PMNode } from "prosemirror-model";
import type { Para } from "../format/types";
import {
  collectSources,
  computeSuggestions,
  type SuggestResult,
  type SuggestionSources,
} from "./suggestions";

export const autocompleteKey = new PluginKey<ACState>("autocomplete");

interface ACState {
  sources: SuggestionSources;
  docRef: PMNode;
  active: { result: SuggestResult; index: number; contentStart: number } | null;
}

/** Lightweight Para[] view of the PM doc for source scanning. */
function scanParas(doc: PMNode): Para[] {
  const paras: Para[] = [];
  doc.forEach((node) => {
    if (node.type.name === "para") {
      paras.push({
        style: String(node.attrs.styleName),
        runs: [{ text: node.textContent }],
      });
    }
  });
  return paras;
}

function caretContext(state: EditorState) {
  const sel = state.selection;
  if (!sel.empty) return null;
  const $from = sel.$from;
  for (let d = $from.depth; d >= 0; d--) {
    const node = $from.node(d);
    if (node.type.name === "para") {
      return {
        element: String(node.attrs.styleName),
        text: node.textContent,
        caret: $from.parentOffset,
        contentStart: $from.start(d),
      };
    }
  }
  return null;
}

function computeActive(
  state: EditorState,
  sources: SuggestionSources,
  sep: string,
): ACState["active"] {
  const ctx = caretContext(state);
  if (!ctx) return null;
  const result = computeSuggestions({
    element: ctx.element,
    text: ctx.text,
    caret: ctx.caret,
    sources,
    sceneTimeSeparator: sep,
  });
  if (!result) return null;
  return { result, index: 0, contentStart: ctx.contentStart };
}

/**
 * Accept the open suggestion, if any. Used by the editor's Android beforeinput
 * fallback so a keyboard Enter accepts the dropdown instead of splitting.
 */
export function acceptActiveSuggestion(view: EditorView): boolean {
  const active = autocompleteKey.getState(view.state)?.active;
  return active ? accept(view, active) : false;
}

/** Apply the currently highlighted suggestion. */
function accept(view: EditorView, active: NonNullable<ACState["active"]>): boolean {
  const item = active.result.items[active.index];
  if (item === undefined) return false;
  const from = active.contentStart + active.result.from;
  const to = active.contentStart + active.result.to;
  const tr = view.state.tr.insertText(item, from, to);
  view.dispatch(tr.scrollIntoView());
  return true;
}

export function autocompletePlugin(dom: Document, sceneTimeSeparator: string): Plugin<ACState> {
  return new Plugin<ACState>({
    key: autocompleteKey,
    state: {
      init(_config, state) {
        const sources = collectSources(dom, scanParas(state.doc), sceneTimeSeparator);
        return {
          sources,
          docRef: state.doc,
          active: computeActive(state, sources, sceneTimeSeparator),
        };
      },
      apply(tr, value, _old, newState) {
        // Rescan sources only when the document actually changed.
        const sources = tr.docChanged
          ? collectSources(dom, scanParas(newState.doc), sceneTimeSeparator)
          : value.sources;

        // Meta-only updates (dropdown navigation / dismissal) keep the caret put.
        const meta = tr.getMeta(autocompleteKey) as
          | { index?: number; close?: boolean }
          | undefined;
        if (meta && !tr.docChanged && !tr.selectionSet) {
          if (meta.close) {
            return { sources, docRef: newState.doc, active: null };
          }
          if (typeof meta.index === "number" && value.active) {
            return {
              sources,
              docRef: newState.doc,
              active: { ...value.active, index: meta.index },
            };
          }
        }

        return {
          sources,
          docRef: newState.doc,
          active: computeActive(newState, sources, sceneTimeSeparator),
        };
      },
    },
    props: {
      handleKeyDown(view, event) {
        const active = autocompleteKey.getState(view.state)?.active;
        if (!active) return false; // never steal keys unless the dropdown is open
        switch (event.key) {
          case "ArrowDown":
          case "ArrowUp": {
            const dir = event.key === "ArrowDown" ? 1 : -1;
            const n = active.result.items.length;
            const index = (active.index + dir + n) % n;
            view.dispatch(view.state.tr.setMeta(autocompleteKey, { index }));
            return true;
          }
          case "Enter":
          case "Tab":
            return accept(view, active);
          case "Escape":
            view.dispatch(view.state.tr.setMeta(autocompleteKey, { close: true }));
            return true;
          default:
            return false;
        }
      },
    },
    view(editorView) {
      return new AutocompleteView(editorView);
    },
  });
}

/** Renders the dropdown as a fixed-position overlay near the caret. */
class AutocompleteView {
  private readonly dom: HTMLDivElement;

  constructor(private readonly view: EditorView) {
    this.dom = document.createElement("div");
    this.dom.className = "sp-ac";
    this.dom.style.display = "none";
    document.body.appendChild(this.dom);
    this.update(view);
  }

  update(view: EditorView) {
    const state = autocompleteKey.getState(view.state);
    const active = state?.active ?? null;
    if (!active) {
      this.hide();
      return;
    }
    this.render(active);
    this.position();
  }

  private render(active: NonNullable<ACState["active"]>) {
    this.dom.textContent = "";
    active.result.items.forEach((item, i) => {
      const el = document.createElement("button");
      el.className = "sp-ac-item" + (i === active.index ? " sp-ac-sel" : "");
      el.textContent = item;
      el.addEventListener("mousedown", (e) => {
        e.preventDefault(); // keep editor focus
        this.view.dispatch(this.view.state.tr.setMeta(autocompleteKey, { index: i }));
        const cur = autocompleteKey.getState(this.view.state)?.active;
        if (cur) accept(this.view, { ...cur, index: i });
      });
      this.dom.appendChild(el);
    });
    this.dom.style.display = "block";
  }

  private position() {
    try {
      const { from } = this.view.state.selection;
      const coords = this.view.coordsAtPos(from);
      this.dom.style.left = `${Math.round(coords.left)}px`;
      this.dom.style.top = `${Math.round(coords.bottom + 2)}px`;
    } catch {
      /* coordsAtPos can throw during transient states; ignore */
    }
  }

  private hide() {
    if (this.dom.style.display !== "none") this.dom.style.display = "none";
  }

  destroy() {
    this.dom.remove();
  }
}
